/**
 * The gate: for a location viewer with no full selection, renders the
 * group→teacher picker INSTEAD of children — no sidebar, no routes behind
 * it. Passthrough for every other viewer kind (Task 2.2,
 * docs/plans/2026-07-30-location-login.md).
 */

import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';

import { useLocations } from '@/hooks/useLocations';
import { PageLoader } from '@/components/ui/page-loader';
import { IdentityErrorRetry } from '@/components/auth/IdentityErrorRetry';
import { useBootContext } from '@/hooks/useBootContext';
import { useIdleReset } from '@/modules/kiosk/hooks/useIdleReset';
import { LOCATION_IDLE_MS } from '../constants';
import { useLocationSession } from '../hooks/useLocationSession';
import { useLocationGroups } from '../hooks/useLocationGroups';
import { useLocationTeachersToday } from '../hooks/useLocationTeachersToday';
import { GroupPicker } from './GroupPicker';
import { TeacherPicker } from './TeacherPicker';
import { RelockSheet } from './RelockSheet';
import type { LocationSessionGroup } from '../types';

export function LocationGate({ children }: { children: ReactNode }) {
  const {
    isLocationViewer,
    isLoading: locationSessionLoading,
    locationCode,
    session,
    lastTeacher,
    select,
    relock,
    reset,
    switchOpen,
    closeSwitch,
  } = useLocationSession();
  const { query: bootQ } = useBootContext();
  const [pendingGroup, setPendingGroup] = useState<LocationSessionGroup | null>(null);

  const activeGroup = session?.group ?? pendingGroup;
  // Also covers the in-modal switch-teacher flow: session.teacher is still
  // live there, but switchOpen means the picker needs teachers+groups too.
  const needsTeacher = isLocationViewer && !!activeGroup && (!session?.teacher || switchOpen);

  // Prefetch, not lazy-load: both lists are fetched as soon as the location is
  // known (i.e. at login), rather than when the picker actually needs them.
  // Measured 2026-08-05 at rb5: 8 teachers = 1.3 KB, groups = a handful of
  // rows -- small enough that fetching them unconditionally costs less than
  // making a teacher wait for a round trip after tapping a group. This matters
  // most for the 30-minute relock, the interaction staff hit all day: the
  // teacher list is already warm when the sheet opens.
  //
  // Deliberately NOT persisted to localStorage (owner ruling 2026-08-05):
  // React Query's in-memory cache + refetch-on-focus keeps the lists fresh as
  // the day changes (sick calls, extra days, switches), which a disk cache
  // would have to reimplement.
  const prefetchLists = isLocationViewer && !!locationCode;
  const groupsQ = useLocationGroups(prefetchLists ? locationCode : null);
  const teachersQ = useLocationTeachersToday(prefetchLists);
  const locationsQ = useLocations({ enabled: isLocationViewer });

  useIdleReset(LOCATION_IDLE_MS, () => { void relock(); }, isLocationViewer && !!session?.teacher);

  // Viewer-kind is still resolving — isLocationViewer defaults to false
  // while pending, so rendering children here would flash the full app
  // (routed page included) on a location device for one frame before the
  // gate kicks in. Never render children until we actually know (Phase 2
  // T3 finding). session-restore itself is synchronous once
  // isLocationViewer resolves (see LocationSessionContext), so there is no
  // separate "session still restoring" window to also guard here.
  //
  // A failed boot (no result at all) keeps the app closed too — the viewer
  // kind is unknown, so offer a retry rather than guessing "not a location
  // account" and opening the full app on a room iPad.
  if (locationSessionLoading) {
    if (bootQ.isError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <IdentityErrorRetry onRetry={() => { void bootQ.refetch(); }} />
        </div>
      );
    }
    return <PageLoader />;
  }

  if (!isLocationViewer) return <>{children}</>;
  if (session?.teacher && !switchOpen) return <>{children}</>;

  if (groupsQ.isError) throw groupsQ.error;
  if (teachersQ.isError) throw teachersQ.error;
  if (locationsQ.isError) throw locationsQ.error;

  // Invariant: isLocationViewer is only ever true when getViewerContext
  // resolved a location_accounts row, which always carries a locationCode
  // (rolesService.getViewerContext) — a null code here means that invariant
  // broke somewhere upstream. Fail loudly rather than silently rendering
  // the picker under an empty/wrong location code (claude-review PR #1050).
  if (!locationCode) throw new Error('LocationGate: isLocationViewer is true but locationCode is null');
  const code = locationCode;

  // A committed session with no teacher only happens via relock() (or a
  // reload mid-relock) — the fresh "never selected yet" flow uses
  // pendingGroup instead, so this branch never fires during first-time
  // onboarding, and TeacherPicker (below) stays the only full-screen path.
  // The switch-modal branch (session.teacher still live, switchOpen true)
  // shares the same sheet — variant alone decides dismissibility.
  if (session && (!session.teacher || switchOpen)) {
    const variant = session.teacher ? 'switch' : 'idle';
    const fetchedGroups = groupsQ.data?.groups ?? [];
    // Campus groups (own + sibling) stay on the sheet — rb3 iPad must
    // still be able to switch into Rabbits/Goats without a toggle.
    // Session group can still be missing from the fetch (inactive /
    // renamed); RelockSheet's initialGroupId always needs to resolve
    // inside `groups` or group.find() toast-and-stalls. Append it last.
    const relockGroups = fetchedGroups.some((g) => g.id === session.group.id)
      ? fetchedGroups
      : [...fetchedGroups, session.group];
    return (
      <>
        {children}
        <RelockSheet
          variant={variant}
          groups={relockGroups}
          teachers={teachersQ.data ?? []}
          isLoading={teachersQ.isLoading || groupsQ.isLoading}
          initialGroupId={session.group.id}
          locationLabel={code.toUpperCase()}
          ownLocationCode={code}
          lastTeacher={variant === 'idle' ? lastTeacher : null}
          onPick={(group, teacher) => select(group, teacher)}
          onDismiss={variant === 'switch' ? closeSwitch : undefined}
        />
      </>
    );
  }

  if (!activeGroup) {
    const locationName = locationsQ.data?.find((l) => l.value === code)?.name ?? code;
    return (
      <GroupPicker
        locationName={locationName}
        locationCode={code}
        groups={groupsQ.data?.groups ?? []}
        siblingLocationCode={groupsQ.data?.siblingLocationCode ?? null}
        onPick={setPendingGroup}
      />
    );
  }

  return (
    <TeacherPicker
      locationCode={code}
      group={activeGroup}
      teachers={teachersQ.data ?? []}
      isLoading={teachersQ.isLoading}
      onBack={async () => {
        if (session) await reset();
        setPendingGroup(null);
      }}
      onPick={async (teacher) => {
        try {
          await select(activeGroup, teacher);
          setPendingGroup(null);
        } catch (err) {
          // Keep the picker usable on failure — do NOT clear pendingGroup,
          // the teacher stays on the picker to retry (this exact silent
          // failure hid a broken RB5 kiosk for weeks).
          toast.error(err instanceof Error ? err.message : 'Failed to select teacher — try again');
        }
      }}
    />
  );
}
